import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon/Icon";
import { logEvent } from "@/lib/eventLogger";
import type { FormulationEntry } from "@/utils/types/compound";
import type { VersionDetail } from "@/utils/types/flavorVersion";
import { useImportParse } from "@/hooks/useImport";
import { useCurrentFormulation } from "@/hooks/useCurrentFormulation";
import { buildFlaskTsv } from "@/utils/export/buildTsv";
import { saveFlaskExcel } from "@/utils/export/saveExcel";
import type { ImportParseResult } from "@/utils/types/flavorImport";
import { HistoryPopover } from "../HistoryPopover";
import { ImportResultsModal } from "../ImportResultsModal";
import { ImportForm } from "../ImportForm";

// Overflow ⋯ menu for the Flask toolbar.
//
// Layout:
//   ⋯ → root popover
//       ├─ History     → nested popover with HistoryPopover (720px)
//       ├─ Import      → nested popover with ImportForm (340px)
//       ├─ Copy as TSV (action)
//       ├─ Save as Excel (action)
//       └─ Print (action)
//
// History/Import each open as a nested view with a left-arrow back into root.
// Export rows are one-shot actions and close the menu on click.
//
// ImportResultsModal renders at the menu's root so it persists after the
// popover closes on parse success.

type FlaskMoreMenuProps = {
  formulation: FormulationEntry[];
  foundation: FormulationEntry[];
  flavorId: string | null;
  formulaId: number | null;
  flavorName?: string;
  formulaName?: string;
  onRestoreSuccess?: (detail: VersionDetail) => void;
  onImportCommitSuccess?: (detail: VersionDetail) => void;
};

type View = null | "root" | "history" | "import";

export function FlaskMoreMenu({
  formulation,
  foundation,
  flavorId,
  formulaId,
  flavorName,
  formulaName,
  onRestoreSuccess,
  onImportCommitSuccess,
}: FlaskMoreMenuProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<View>(null);
  const [parseResult, setParseResult] = useState<ImportParseResult | null>(null);

  // Outside-click closes the menu (but only after the user actively opened it).
  useEffect(() => {
    if (!view) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setView(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [view]);

  // Export — same server-canonical item list the standalone Export popover
  // used previously. Print opens the existing print route in a new tab.
  const cur = useCurrentFormulation(flavorId ?? "", formulaId ?? 0);
  const exportItems = cur.data?.items ?? [];
  const exportDisabled = !flavorId || !formulaId || exportItems.length === 0;

  const onCopyTsv = async () => {
    logEvent("flavor_workbench_flask_tool_clicked", {
      tool: "copy_tsv",
      flavor_id: flavorId,
      formula_id: formulaId,
      item_count: exportItems.length,
    });
    try {
      await navigator.clipboard.writeText(buildFlaskTsv(exportItems));
    } catch {
      // clipboard may be blocked in non-https contexts — silent failure is fine
    }
    setView(null);
  };
  const onSaveExcel = async () => {
    logEvent("flavor_workbench_flask_tool_clicked", {
      tool: "save_excel",
      flavor_id: flavorId,
      formula_id: formulaId,
      item_count: exportItems.length,
    });
    await saveFlaskExcel({
      items: exportItems,
      flavorName: flavorName ?? "flask",
      formulaName: formulaName ?? "formula",
    });
    setView(null);
  };
  const onPrint = () => {
    if (!flavorId || formulaId == null) return;
    logEvent("flavor_workbench_flask_tool_clicked", {
      tool: "print",
      flavor_id: flavorId,
      formula_id: formulaId,
      item_count: exportItems.length,
    });
    window.open(`/print/flavor/${flavorId}/formulas/${formulaId}`, "_blank");
    setView(null);
  };

  // Import parse mutation — only meaningful when flavorId + formulaId are known.
  // The Import row is disabled in that case so the cast is safe.
  const importParse = useImportParse(flavorId ?? "", formulaId ?? 0);
  const importDisabled = !flavorId || formulaId == null;
  const historyDisabled = !flavorId || !formulaId;

  const handleImportSubmit = async (args: { text?: string; fileUploadId?: string }) => {
    if (!flavorId || formulaId == null) return;
    try {
      if (args.text && args.text.trim().length > 0) {
        logEvent("flavor_workbench_import_submitted", {
          flavor_id: flavorId,
          formula_id: formulaId,
          input_type: "text",
          file_upload_id: null,
          text_char_count: args.text.length,
        });
        const result = await importParse.mutateAsync({ kind: "paste", text: args.text });
        setView(null);
        setParseResult(result);
      } else if (args.fileUploadId) {
        logEvent("flavor_workbench_import_submitted", {
          flavor_id: flavorId,
          formula_id: formulaId,
          input_type: "file",
          file_upload_id: args.fileUploadId,
          text_char_count: null,
        });
        const result = await importParse.mutateAsync({
          kind: "file",
          fileUploadId: args.fileUploadId,
        });
        setView(null);
        setParseResult(result);
      }
    } catch {
      // importParse.isError surfaces inside ImportForm; nothing more to do
    }
  };

  // Avoid an unused-variable warning while still keeping the foundation prop
  // visible at the call site (kept for future export-related concerns).
  void foundation;
  void formulation;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="btn xs"
        onClick={() => setView((v) => (v === "root" ? null : "root"))}
        title="More actions"
        style={{ padding: "3px 5px" }}
      >
        <Icon name="dots" size={14} />
      </button>

      {view === "root" && (
        <div className="wb-pop" style={{ width: 220 }}>
          <div className="wb-pop-header">
            <span className="label">More actions</span>
          </div>
          <div className="vstack" style={{ gap: 1, padding: 6 }}>
            <button
              type="button"
              className="af-overflow-row"
              onClick={() => {
                logEvent("flavor_workbench_flask_tool_clicked", {
                  tool: "history",
                  flavor_id: flavorId,
                  formula_id: formulaId,
                });
                setView("history");
              }}
              disabled={historyDisabled}
              title={
                !flavorId
                  ? "Save the flavor first to enable history"
                  : !formulaId
                  ? "First edit will be saved automatically; history appears after"
                  : "Formulation history"
              }
            >
              <Icon name="history" size={12} />
              <span style={{ flex: 1 }}>History</span>
              <Icon name="chevron-r" size={10} />
            </button>
            <button
              type="button"
              className="af-overflow-row"
              onClick={() => {
                logEvent("flavor_workbench_flask_tool_clicked", {
                  tool: "import",
                  flavor_id: flavorId,
                  formula_id: formulaId,
                });
                setView("import");
              }}
              disabled={importDisabled}
              title={
                !flavorId
                  ? "Save the flavor first to enable import"
                  : formulaId == null
                  ? "First edit will be saved automatically; import appears after"
                  : "Import formulation from file or paste"
              }
            >
              <Icon name="upload" size={12} />
              <span style={{ flex: 1 }}>Import</span>
              <Icon name="chevron-r" size={10} />
            </button>
            <div
              style={{ height: 1, background: "var(--line-2)", margin: "4px 0" }}
            />
            <button
              type="button"
              className="af-overflow-row"
              onClick={onCopyTsv}
              disabled={exportDisabled}
            >
              <Icon name="copy" size={12} />
              <span style={{ flex: 1 }}>Copy as TSV</span>
            </button>
            <button
              type="button"
              className="af-overflow-row"
              onClick={onSaveExcel}
              disabled={exportDisabled}
            >
              <Icon name="table" size={12} />
              <span style={{ flex: 1 }}>Save as Excel</span>
            </button>
            <button
              type="button"
              className="af-overflow-row"
              onClick={onPrint}
              disabled={exportDisabled}
            >
              <Icon name="printer" size={12} />
              <span style={{ flex: 1 }}>Print</span>
            </button>
          </div>
        </div>
      )}

      {view === "history" && flavorId && formulaId && (
        <div className="wb-pop" style={{ width: 720 }}>
          <div className="wb-pop-header">
            <span className="label">
              <button
                type="button"
                onClick={() => setView("root")}
                aria-label="Back"
                style={{
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  marginRight: 6,
                  color: "var(--ink-4)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <Icon name="arrow-l" size={11} />
              </button>
              Formulation history
            </span>
          </div>
          <HistoryPopover
            flavorId={flavorId}
            formulaId={formulaId}
            onClose={() => setView(null)}
            onRestoreSuccess={onRestoreSuccess}
          />
        </div>
      )}

      {view === "import" && (
        <div className="wb-pop" style={{ width: 340 }}>
          <div className="wb-pop-header">
            <span className="label">
              <button
                type="button"
                onClick={() => setView("root")}
                aria-label="Back"
                style={{
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  marginRight: 6,
                  color: "var(--ink-4)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <Icon name="arrow-l" size={11} />
              </button>
              Import formulation
            </span>
            <span className="mono" style={{ fontSize: 9, color: "var(--ink-4)" }}>
              CSV · TSV · XLSX · JSON · PDF
            </span>
          </div>
          <ImportForm
            onClose={() => setView(null)}
            onSubmit={handleImportSubmit}
            isPending={importParse.isPending}
            isError={importParse.isError}
            flavorFormulaId={formulaId}
          />
        </div>
      )}

      {parseResult && flavorId && formulaId != null && (
        <ImportResultsModal
          flavorId={flavorId}
          formulaId={formulaId}
          parseResult={parseResult}
          currentFlaskEmpty={exportItems.length === 0}
          onClose={() => setParseResult(null)}
          onCommitSuccess={onImportCommitSuccess}
        />
      )}
    </div>
  );
}
